import React, { useState, useMemo } from "react";
import { TableColumnsType, Tag, Tooltip } from "antd";
import { Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkBoqAside from "./workBoqAside";

interface WorkBoqApprovalData {
  id: string;
  boqTitle: string;
  siteName: string;
  workLevel: string;
  contractor: string;
  totalLines: number;
  status: "Pending Approval" | "Approved" | "Rejected";
  submittedAt: string;
  submittedBy: string;
  approvalLevel: string;
  currentApprover: string;
}

const WorkBoqApprovals: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
  const isClientFlow = flowType?.toLowerCase() === "client";
  const [sm, setSm] = useState(false);

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      Pending: "processing",
      Approved: "success",
      Rejected: "error",
      Submitted: "processing",
    };
    return statusColors[status] || "default";
  };

  const columns: TableColumnsType<WorkBoqApprovalData> = useMemo(
    () => [
      {
        title: "BOQ ID",
        dataIndex: "workBoqSequenceId",
        key: "workBoqSequenceId",
        width: 100,
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: "BOQ Title",
        dataIndex: "boqTitle",
        key: "boqTitle",
        width: 250,
        render: (text, record: any) => (
          <div>
            <Tooltip title={text}>
              <Link
                to={`/work-boq-view/${record.workBoqId}${flowTypeQuery}`}
                state={{ fromPage: "approvals" }}
                className="fw-bold text-primary"
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "220px",
                }}
              >
                {text}
              </Link>
            </Tooltip>
            {/* <div className="text-muted small mt-1">{record.boqDescription}</div> */}
            {record.siteName && (
              <div className="text-muted small mt-1">
                Site: {record.siteName}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Work Level",
        dataIndex: "segmentLevel",
        key: "segmentLevel",
        width: 120,
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: isClientFlow ? "Client" : "Contractor",
        dataIndex: "contractorName",
        key: "contractorName",
        width: 220,
      },
      {
        title: "Lines",
        dataIndex: "totalLines",
        key: "totalLines",
        width: 80,
        align: "center",
        render: (text) => <span className="badge badge-soft-info">{text}</span>,
      },
      // {
      //   title: "Status",
      //   dataIndex: "status",
      //   key: "status",
      //   width: 140,
      //   render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      // },
      {
        title: "Approval Status",
        dataIndex: "approvalStatus",
        key: "approvalStatus",
        width: 140,
        render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      },
      {
        title: "Approval Level",
        dataIndex: "approvalLevel",
        key: "approvalLevel",
        width: 140,
        render: (text, record) => (
          <div>
            <div className="fw-bold">{text}</div>
            <small className="text-muted">{record.currentApprover}</small>
          </div>
        ),
      },
      {
        title: "Submitted",
        dataIndex: "submittedAt",
        key: "submittedAt",
        width: 140,
        render: (text, record: any) => (
          <div>
            <div>{dayjs(text).format("DD-MMM-YYYY")}</div>
            <small className="text-muted">by {record.createdByName}</small>
          </div>
        ),
      },
    ],
    [flowType, isClientFlow, flowTypeQuery]
  );

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-boq-approvals"
      title="BOQ Approvals"
      columns={columns}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkBoqAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1100,
      }}
    />
  );
};

export default WorkBoqApprovals;
